/**
 * The keyboard's way into a requirement's detail.
 *
 * A REAL BUTTON, AND THAT IS THE WHOLE POINT. The desktop `<tr>` also opens the
 * drawer on click, but a table row cannot be reached with a keyboard and must
 * not pretend otherwise: `role="button"` on a `<tr>` breaks the table semantics
 * a screen reader relies on to say "row 3 of 12, column Status", and `tabIndex`
 * on it puts a stop in the tab order with no announced role. So the row click
 * stays what it is — mouse comfort — and this button is the affordance that
 * every input method shares.
 *
 * It lives in its own file rather than inside `RequirementsTable` because both
 * layouts need it, and because that file was already at ~465 lines before this
 * slice (CLAUDE.md Regel 48). Two call sites, one definition.
 *
 * THE LABEL NAMES THE ROW. "Details" repeated twelve times down a column is
 * twelve identical entries in a screen reader's element list, with nothing to
 * choose between them. The visible word stays short; the accessible name says
 * which requirement of which person it opens.
 */
import React from "react";
import { ChevronRight } from "lucide-react";

import { requirementTypeLabel } from "./requirementLabels";

/**
 * @param {{
 *   row: object,
 *   onOpen: (row: object) => void,
 *   className?: string,
 * }} props
 */
export default function RequirementDetailTrigger({ row, onOpen, className = "" }) {
  if (typeof onOpen !== "function") return null;

  const label = `${requirementTypeLabel(row.requirement_type)} von ${
    row.tenant_name || "Mieter"
  } anzeigen`;

  return (
    <button
      type="button"
      aria-label={label}
      data-testid={`requirement-details-${row.tenancy_id}:${row.requirement_type}`}
      onClick={(e) => {
        // The row underneath opens the same drawer. Without this the click
        // would arrive twice — once here, once on the <tr> — and a future
        // toggle-style handler would open and immediately close it.
        e.stopPropagation();
        onOpen(row);
      }}
      className={`inline-flex items-center gap-0.5 rounded-[6px] px-1.5 py-1 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${className}`}
    >
      Details
      <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    </button>
  );
}
